import { Injectable } from '@angular/core';
import { CustomProductSearchPage, CustomSearchCriteria } from '@catalog/const';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OccFacetsService, SearchActions } from '@search/data-access';
import { ProductActions, RoutingService } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ActiveFacetsService, SearchFacetService } from '../services';

@Injectable()
export class FacetsEffects {
  loadProductCount$: Observable<
    SearchActions.SearchFacetsTotalResultsLoadSuccess | SearchActions.SearchFacetsTotalResultsLoadFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchActions.LOAD_SEARCH_FACETS_TOTAL_RESULTS),
      map(({ payload }) => payload),
      switchMap((queryParams) => this.loadProducts(queryParams)),
    ),
  );

  saveCountAfterLoadProducts$: Observable<SearchActions.SearchFacetsTotalResultsLoadSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.SEARCH_PRODUCTS_SUCCESS),
      map(({ payload }) => payload),
      concatMap(({ pagination: { totalResults } }: CustomProductSearchPage) =>
        of(new SearchActions.SearchFacetsTotalResultsLoadSuccess(totalResults)),
      ),
    ),
  );

  changeFacetValue$: Observable<SearchActions.SearchFacetsTotalResultsLoad> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SearchActions.TOGGLE_SELECTED_FACET,
        SearchActions.CLEAR_FACET_VALUE,
        SearchActions.SET_SELECTED_FACET_VALUE,
        SearchActions.SET_SELECTED_FACET_RANGE_VALUE,
        SearchActions.CLEAR_SELECTED_FACETS,
      ),
      debounceTime(100),
      withLatestFrom(this.activeFacetsService.getSelectedFacetParams(), this.routing.getRouterState()),
      map(([, searchFacetParams, routerState]) => ({
        searchFacetParams,
        routerState,
      })),
      map(({ searchFacetParams, routerState }) => {
        const freeTextSearch = routerState.state.queryParams.freeTextSearch;
        const facets = SearchFacetService.createSearchQuery(searchFacetParams);

        return new SearchActions.SearchFacetsTotalResultsLoad({ freeTextSearch, facets });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private occFacetsService: OccFacetsService,
    private activeFacetsService: ActiveFacetsService,
    private routing: RoutingService,
  ) {}

  private loadProducts(
    queryParams: CustomSearchCriteria,
  ): Observable<SearchActions.SearchFacetsTotalResultsLoadSuccess | SearchActions.SearchFacetsTotalResultsLoadFail> {
    return this.occFacetsService.loadProductCount(queryParams).pipe(
      map(({ pagination: { totalResults } }) => new SearchActions.SearchFacetsTotalResultsLoadSuccess(totalResults)),
      catchError((error) => of(new SearchActions.SearchFacetsTotalResultsLoadFail(error))),
    );
  }
}
